import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDraftHistory, restoreDraftHistory,
  type DraftHistoryEntry, type ListingPayload,
} from '@/api/play';
import { playLocaleName } from '@/lib/playLocales';
import { Modal } from '../Modal';
import { Button } from '../Button';

const TYPE_LABEL: Record<string, string> = {
  icon: 'icon', featureGraphic: 'feature graphic', phoneScreenshots: 'screenshots',
};

function entryLabel(e: DraftHistoryEntry): string {
  if (e.kind === 'image') return e.imageType ? (TYPE_LABEL[e.imageType] ?? e.imageType) : 'image';
  const p = e.payload as ListingPayload;
  return p.title ? `text · “${p.title}”` : 'text';
}

const fmt = (s: number) => new Date(s * 1000).toLocaleString();

// Lets a previously-submitted (and therefore cleared) draft be restored back
// into the editable drafts. Entries are grouped by submission timestamp.
export function DraftHistoryModal({ appId, onClose }: { appId: number; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: entries, isLoading, error } = useQuery({
    queryKey: ['play-draft-history', appId],
    queryFn: () => getDraftHistory(appId),
  });

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);

  const groups = useMemo(() => {
    const map = new Map<number, DraftHistoryEntry[]>();
    for (const e of entries ?? []) {
      const arr = map.get(e.submitted_at) ?? [];
      arr.push(e);
      map.set(e.submitted_at, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => b - a); // newest first
  }, [entries]);

  const toggle = (id: number) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleGroup = (ids: number[], on: boolean) => setSelected((prev) => {
    const next = new Set(prev);
    for (const id of ids) (on ? next.add(id) : next.delete(id));
    return next;
  });

  const restoreMut = useMutation({
    mutationFn: () => restoreDraftHistory(appId, Array.from(selected)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['play-drafts', appId] });
      qc.invalidateQueries({ queryKey: ['play-snapshot', appId] });
      onClose();
    },
    onError: (e: any) => setActionError(e?.message ?? 'Restore failed'),
  });

  return (
    <Modal isOpen onClose={onClose} title="Submission history" size="md">
      <p className="text-sm text-content-muted mb-3">
        Drafts are archived when submitted. Restore any of them back into your editable drafts —
        it overwrites the current draft for that locale, the live listing is untouched.
      </p>

      {isLoading ? (
        <p className="text-content-muted text-sm">Loading…</p>
      ) : error ? (
        <p className="text-red-400 text-sm">{(error as Error).message}</p>
      ) : groups.length === 0 ? (
        <p className="text-content-muted text-sm">No submissions yet.</p>
      ) : (
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {groups.map(([ts, items]) => {
            const ids = items.map((e) => e.id);
            const allOn = ids.every((id) => selected.has(id));
            return (
              <div key={ts} className="border border-border rounded-md">
                <label className="flex items-center gap-2 px-3 py-2 border-b border-border cursor-pointer bg-surface-muted/40">
                  <input type="checkbox" checked={allOn} onChange={(e) => toggleGroup(ids, e.target.checked)} />
                  <span className="text-xs font-medium text-content">{fmt(ts)}</span>
                  <span className="ml-auto text-xs text-content-muted">{items.length} item(s)</span>
                </label>
                <div className="divide-y divide-border">
                  {items.map((e) => (
                    <label key={e.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-surface-muted">
                      <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} />
                      <span className="text-sm text-content">{e.language}</span>
                      <span className="text-xs text-content-muted">{playLocaleName(e.language) ?? ''}</span>
                      <span className="ml-auto text-xs text-content-muted">{entryLabel(e)}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {actionError && <p className="text-red-400 text-sm mt-3">{actionError}</p>}
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="secondary" onClick={onClose} disabled={restoreMut.isPending}>Close</Button>
        <Button disabled={selected.size === 0} loading={restoreMut.isPending} onClick={() => restoreMut.mutate()}>
          {selected.size > 0 ? `Restore ${selected.size}` : 'Restore'}
        </Button>
      </div>
    </Modal>
  );
}
